import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Check, FileText, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/orders/${orderId}`, { headers })
      .then(res => setOrder(res.data)).catch(console.error).finally(() => setLoading(false));
  }, [orderId, token]);

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>;
  if (!order) return <div className="text-center py-12 text-slate-500">Objednavka nenalezena</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="order-confirmation-page">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-emerald-600" /></div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Dekujeme za objednavku!</h1>
        <p className="text-slate-500">Cislo objednavky: <span className="font-medium text-[#004E98]">{order.order_number}</span></p>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <h2 className="font-bold text-slate-800 mb-4">Detail objednavky</h2>
        <div className="space-y-3">
          {order.items.map((item, i) => (<div key={i} className="flex justify-between"><span className="text-slate-600">{item.name} x {item.quantity}</span><span className="font-medium">{item.total.toFixed(2)} Kc</span></div>))}
        </div>
        <div className="border-t border-slate-200 mt-4 pt-4 space-y-2">
          <div className="flex justify-between text-slate-600"><span>Mezisoucet</span><span>{order.subtotal.toFixed(2)} Kc</span></div>
          {order.discount > 0 && (<div className="flex justify-between text-emerald-600"><span>Sleva{order.coupon_code ? ` (${order.coupon_code})` : ''}</span><span>-{order.discount.toFixed(2)} Kc</span></div>)}
          <div className="flex justify-between text-slate-600"><span>Doprava</span><span>{order.shipping_cost.toFixed(2)} Kc</span></div>
          <div className="flex justify-between text-lg font-bold text-slate-800"><span>Celkem</span><span className="text-[#004E98]">{order.total.toFixed(2)} Kc</span></div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-semibold text-slate-800 mb-3">Fakturacni adresa</h3>
          <p className="text-slate-600 text-sm">
            {order.billing_address.firstname} {order.billing_address.lastname}<br/>
            {order.billing_address.company && <>{order.billing_address.company}<br/></>}
            {order.billing_address.address1}<br/>
            {order.billing_address.postcode} {order.billing_address.city}<br/>
            {order.billing_address.phone}<br/>{order.billing_address.email}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h3 className="font-semibold text-slate-800 mb-3">Zpusob platby</h3>
          <p className="text-slate-600 text-sm">
            {order.payment_method === 'card' && 'Platba kartou online'}
            {order.payment_method === 'invoice' && 'Faktura (platba prevodem)'}
            {order.payment_method === 'cash' && 'Hotove pri prevzeti'}
            {order.payment_method === 'cod' && 'Dobirka'}
          </p>
          {order.payment_status && (<p className={`text-sm mt-2 font-medium ${order.payment_status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>{order.payment_status === 'paid' ? 'Zaplaceno' : 'Ceka na platbu'}</p>)}
        </div>
      </div>
      <div className="text-center mb-8">
        <a href={`${API}/orders/${order.id}/invoice`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#004E98] text-white rounded text-sm font-medium hover:bg-[#003B73] transition-colors" data-testid="download-invoice-btn"><FileText className="w-4 h-4" />Stahnout fakturu (PDF)</a>
      </div>
      <div className="text-center"><Link to="/produkty" className="inline-block bg-[#004E98] text-white hover:bg-[#003B73] rounded-sm px-6 py-3 font-medium uppercase tracking-wide text-sm transition-all">Pokracovat v nakupu</Link></div>
    </div>
  );
}
